import { NgModule } from '@angular/core';
import { ExtraOptions, PreloadAllModules, RouterModule, Routes } from '@angular/router';
import { NoAuthGuard } from '@core/auth/guards/noAuth.guard';
import { LayoutComponent } from './layout/layout.component';
import { AuthGuard } from '@core/auth/guards/auth.guard';
import { BrokerPendingAuthGuard } from '@core/auth/guards/broker-pending-auth.guard';
import { TwoStepPendingAuthGuard } from '@core/auth/guards/two-step-pending-auth.guard';
import { MfaPendingAuthGuard } from '@core/auth/guards/mfa-pending-auth.guard';
import { InitialDataResolver } from './app.resolver';
import { AdministrationResolver } from './modules/General/admin/admin.resolver';
import { SettingsResolver } from './modules/Branches/Automobile/settings/settings.resolver';
import { AttestationsResolver } from './modules/Branches/Automobile/attestations/attestations.resolver';
import { InsuredResolver } from './modules/General/insured/insured.resolver';
import { ProductionResolver } from '@core/resolvers/production/production.resolver';
import { BrokerProfileGuard } from '@core/services/auth/broker-profile/broker-profile.guard';
import { provideTranslocoScope } from '@jsverse/transloco';

const routerConfig: ExtraOptions = {
  scrollPositionRestoration: 'enabled',
  // Précharge les chunks lazy en arrière-plan après le bootstrap : au clic sur un
  // menu, le module est déjà en mémoire et les requêtes du resolver partent immédiatement.
  preloadingStrategy: PreloadAllModules
};


const routes: Routes = [
    {path: '', pathMatch : 'full', redirectTo: 'home'},

    {path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home'},
    {path: 'signed-up-redirect', pathMatch: 'full', redirectTo: 'home'},

    // Public (unauthenticated) document viewer accessed through an opaque token.
    // No guard: anyone with the link can view the vehicle attestations.
    {
        path: 'd/:token',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {
                path: '',
                loadComponent: () => import('./modules/Public/public-document/public-document.component').then(m => m.PublicDocumentComponent)
            }
        ]
    },
    {
        // Same public viewer, with the language carried as the first segment: /{lang}/d/{token}.
        // Only matches when the 2nd segment is literally "d", so it never shadows other routes.
        path: ':lang/d/:token',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {
                path: '',
                loadComponent: () => import('./modules/Public/public-document/public-document.component').then(m => m.PublicDocumentComponent)
            }
        ]
    },

    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        loadChildren: () => import('./modules/Base/auth/auth.module').then(m => m.AuthModule)
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        providers: [provideTranslocoScope('auth')],
        children: [
            {path: 'sign-out', loadComponent: () => import('./modules/Base/auth/sign-out/sign-out.component').then(m => m.AuthSignOutComponent)},
        ]
    },

    // Broker profile selection route - requires pending broker authentication (temporaryToken)
    {
        path: '',
        canActivate: [BrokerPendingAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        providers: [provideTranslocoScope('auth')],
        children: [
            {path: 'select-profile', loadComponent: () => import('./modules/Base/auth/select-profile/select-profile.component').then(m => m.AuthSelectProfileComponent)},
        ]
    },

    // MFA verification route - requires pending MFA authentication (mfaToken)
    {
        path: '',
        canActivate: [MfaPendingAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        providers: [provideTranslocoScope('auth')],
        children: [
            {path: 'mfa-verification', loadComponent: () => import('./modules/Base/auth/mfa-verification/mfa-verification.component').then(m => m.AuthMfaVerificationComponent)},
        ]
    },

    // CONTRIBUTOR / SUB_CONTRIBUTOR multi-step selection route
    {
        path: '',
        canActivate: [TwoStepPendingAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        providers: [provideTranslocoScope('auth')],
        children: [
            {path: 'multi-step-selection', loadComponent: () => import('./modules/Base/auth/multi-step-selection/multi-step-selection.component').then(m => m.AuthMultiStepSelectionComponent)},
        ]
    },
    {
        path       : '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component  : LayoutComponent,
        resolve    : {
            initialData: InitialDataResolver,
        },
        children   : [
            {
                path: 'home',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                data: {
                    layout: 'custom',
                },
                loadChildren: () => import('./modules/Base/home/home.module').then(m => m.HomeModule)
            },
            {
                path: 'dashboard',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                data: {},
                loadChildren: () => import('./modules/Base/dashboard/dashboard.module').then(m => m.DashboardModule),
            },
            {
                path: 'administration',
                resolve: {
                    administrationData: AdministrationResolver
                },
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                data: {},
                loadChildren: () => import('./modules/General/admin/admin.module').then(m => m.AdminModule)
            },
            {
                path: 'parameters',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                resolve: {
                    resolveData: SettingsResolver
                },
                data: {},
                loadChildren: () => import('./modules/Branches/Automobile/settings/settings.module').then(m => m.SettingsModule)
            },
            {
                path: 'attestations',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                data: {},
                resolve: {
                    AttestationsResolver
                },
                loadChildren: () => import('./modules/Branches/Automobile/attestations/attestations.module').then(m => m.AttestationsModule)
            },
            {
                path: 'insured',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                data: {
                },
                resolve: {
                    InsuredResolver
                },
                loadChildren: () => import('./modules/General/insured/insured.module').then(m => m.InsuredModule)
            },

            {
                path: 'production',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                data: {
                },
                resolve: {
                    ProductionResolver
                },
                loadChildren: () => import('./modules/Branches/Automobile/production/production.module').then(m => m.ProductionModule)
            },

            {
                path: 'pricing',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                data: {
                },
                resolve: {
                    pricingData: () => import('./modules/General/pricing//pricing.resolver').then(m => m.PricingResolver)
                },
                loadChildren: () => import('./modules/General/pricing//pricing.module').then(m => m.PricingModule)
            },
          {
            path: 'subscription-rules',
            canActivate: [BrokerProfileGuard],
            canActivateChild: [BrokerProfileGuard],
            data: {
            },
            resolve: {
              pricingData: () => import('./modules/General/subscription-rules/subscription-rules.resolver').then(m => m.SubscriptionRulesResolver)
            },
            loadChildren: () => import('./modules/General/subscription-rules/subscription-rules.module').then(m => m.SubscriptionRulesModule)
          },
            {
                path: 'my-profile',
                canActivate: [BrokerProfileGuard],
                canActivateChild: [BrokerProfileGuard],
                loadChildren: () => import('./modules/Base/my-profile/my-profile.module').then(m => m.MyProfileModule),
            },

            // 404 & Catch all
            {path: '404-not-found', loadChildren: () => import('./modules/Base/error/error.module').then(m => m.ErrorModule), data: {layout: "empty"}},
            {path: '**', redirectTo: '404-not-found'},
        ]
    },
];


@NgModule({
  imports: [RouterModule.forRoot(routes, routerConfig)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
